import type {
  LotCar,
  LotCoordinates,
  TestDriveCenterMake
} from '~/contracts/dealership';

// Filter chip order from the design (most-stocked makes first).
export const MAKE_ORDER: TestDriveCenterMake[] = [
  'Jeep',
  'RAM',
  'Dodge',
  'Chrysler'
];

export type MakeFilter = 'All' | TestDriveCenterMake;

export type MakeChip = { count: number; label: string; value: MakeFilter };

export type MapDot = {
  selected: boolean;
  title: string;
  von: string;
  x: number;
  y: number;
};

export type InertDot = { von: string; x: number; y: number };

export type RailCar = {
  alt: string;
  imgPath: string;
  makeLine: string;
  model: string;
  selected: boolean;
  trim: string;
  von: string;
};

// Drivable cars only — disabled cars are inert on the map.
function getAvailableCars(cars: LotCar[]): LotCar[] {
  return cars.filter(car => !car.disabled);
}

function matchesMake(car: LotCar, make: MakeFilter): boolean {
  return make === 'All' || car.make === make;
}

export function getMakeChips(cars: LotCar[]): MakeChip[] {
  const available = getAvailableCars(cars);
  const counts = available.reduce<Record<string, number>>((acc, car) => {
    acc[car.make] = (acc[car.make] ?? 0) + 1;

    return acc;
  }, {});

  const makeChips: MakeChip[] = MAKE_ORDER.filter(make => counts[make]).map(
    make => ({ count: counts[make], label: make, value: make })
  );

  return [
    { count: available.length, label: 'All', value: 'All' },
    ...makeChips
  ];
}

export function getMapDots(
  cars: LotCar[],
  coords: LotCoordinates,
  make: MakeFilter,
  selectedVon: string
): MapDot[] {
  return getAvailableCars(cars)
    .filter(car => coords[car.von] && matchesMake(car, make))
    .map(car => {
      const [x, y] = coords[car.von];

      return {
        selected: car.von === selectedVon,
        title: `${car.make} ${car.model} ${car.trim}`,
        von: car.von,
        x,
        y
      };
    });
}

// Inert dots: available cars filtered out by the active make, plus every disabled
// car that has a placed coordinate.
export function getInertDots(
  cars: LotCar[],
  coords: LotCoordinates,
  make: MakeFilter
): InertDot[] {
  const filteredOut = getAvailableCars(cars).filter(
    car => coords[car.von] && !matchesMake(car, make)
  );
  const disabled = cars.filter(car => car.disabled && coords[car.von]);

  return [...filteredOut, ...disabled].map(car => {
    const [x, y] = coords[car.von];

    return { von: car.von, x, y };
  });
}

export function getRailCars(
  cars: LotCar[],
  coords: LotCoordinates,
  make: MakeFilter,
  selectedVon: string
): RailCar[] {
  return getAvailableCars(cars)
    .filter(car => coords[car.von] && matchesMake(car, make))
    .map(car => ({
      alt: `${car.year} ${car.make} ${car.model} ${car.trim}`,
      imgPath: car.imgPath,
      makeLine: `${car.year} · ${car.make}`,
      model: car.model,
      selected: car.von === selectedVon,
      trim: car.trim,
      von: car.von
    }));
}

export function getSelectedCar(
  cars: LotCar[],
  selectedVon: string
): LotCar | undefined {
  return cars.find(car => car.von === selectedVon) ?? getAvailableCars(cars)[0];
}

export function getMakeLine(car: LotCar): string {
  return `${car.year} · ${car.make}`;
}

export function getSpecLine(car: LotCar): string {
  return car.dt ? `${car.color} · ${car.dt}` : car.color;
}

export function getZoneLabel(car: LotCar): string {
  // car.id is the stall, e.g. "D2" (zone letter + space number). Drop the leading
  // zone letter from the space so it reads "Zone D · Space 2", not "Space D2".
  return `Zone ${car.id[0]} · Space ${car.id.slice(1)}`;
}
